import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Parse from 'parse'
import { Route } from 'react-router-dom'

const PAGE_SIZE = 100

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'workout', label: 'Workout' },
  { value: 'plan', label: 'Plan' },
  { value: 'conversation', label: 'Conversation' },
  { value: 'analysis', label: 'Analysis' }
]

function renderRating (row) {
  const rating = row.get('thumbRating')
  if (rating === 'up') return '👍'
  if (rating === 'down') return '👎'
  return (<span className="text-muted">—</span>)
}

function renderText (row) {
  const text = row.get('text')
  if (!text) return (<span className="text-muted">—</span>)
  return text
}

function renderUserLink (row) {
  const user = row.get('user')
  if (!user) return (<span className="text-muted">—</span>)
  return (
    <a href={'/#/members/' + user.id}>
      <code>{user.id}</code>
    </a>
  )
}

// Deep-links vary by feedback type. Conversations link via conversationId
// (entityId is a SwimCoachMessage with no admin route). Analysis rows have no
// admin detail route, so their entityId — which is the WorkoutCoachFeedback
// objectId — is shown as plain text.
function renderEntityLink (row) {
  const type = row.get('type')
  const entityId = row.get('entityId')
  if (type === 'workout') {
    if (!entityId) return (<span className="text-muted">—</span>)
    return (<a href={'/#/workouts/' + entityId}><code>{entityId}</code></a>)
  }
  if (type === 'plan') {
    if (!entityId) return (<span className="text-muted">—</span>)
    return (<a href={'/#/trainingplans/' + entityId}><code>{entityId}</code></a>)
  }
  if (type === 'conversation') {
    const conversationId = row.get('conversationId')
    if (!conversationId) return (<span className="text-muted">—</span>)
    return (<a href={'/#/swimCoachConversations/' + conversationId}><code>{conversationId}</code></a>)
  }
  if (type === 'analysis') {
    if (!entityId) return (<span className="text-muted">—</span>)
    return (<code>{entityId}</code>)
  }
  return (<span className="text-muted">—</span>)
}

export default class AIFeedbackListComponent extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      rows: null,
      typeFilter: 'all',
      loadError: null
    }
    this.activeRequestId = 0
    this.loadContent()
  }

  loadContent = async () => {
    // Tag this load so a response from a superseded filter selection can't
    // overwrite the latest results if requests resolve out of order.
    const requestId = ++this.activeRequestId
    const applyState = (next) => {
      if (requestId === this.activeRequestId) this.setState(next)
    }

    const { typeFilter } = this.state
    try {
      const query = new Parse.Query('AiFeedback')
      query.descending('createdAt')
      query.limit(PAGE_SIZE)
      query.include('user')
      // When a specific type is selected, filter server-side; 'all' omits it.
      if (typeFilter !== 'all') {
        query.equalTo('type', typeFilter)
      }
      const results = await query.find()
      applyState({ rows: results, loadError: null })
    } catch (error) {
      console.error('Error loading AI feedback:', error)
      applyState({ loadError: error.message })
    }
  }

  handleTypeFilterChange = (event) => {
    // Re-query the backend so the filter applies across all feedback rather
    // than only the page currently loaded in memory.
    this.setState(
      {
        typeFilter: event.target.value,
        rows: null,
        loadError: null
      },
      this.loadContent
    )
  }

  numberOfResultsText = () => {
    const { rows, loadError } = this.state
    if (loadError) return null
    if (!rows) return (<i>Loading...</i>)
    if (rows.length === PAGE_SIZE) {
      return (<i>Showing first {PAGE_SIZE} (more may exist)</i>)
    }
    return (<i>{rows.length} loaded</i>)
  }

  renderErrorBanner = () => {
    if (!this.state.loadError) return null
    return (
      <div className="alert alert-danger" role="alert">
        <b>Error loading AI feedback:</b> {this.state.loadError}
      </div>
    )
  }

  renderFilterControls = () => {
    return (
      <div className="d-flex align-items-center mb-3">
        <label htmlFor="typeFilter" className="me-2">Type:</label>
        <select
          id="typeFilter"
          className="form-control form-control-sm"
          value={this.state.typeFilter}
          onChange={this.handleTypeFilterChange}
        >
          {TYPE_FILTERS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    )
  }

  renderTableHeader = () => {
    return (
      <tr key={'0-header'} className="data">
        <th className="bg-primary" key={'0h'}>Date ▾</th>
        <th className="bg-primary" key={'1h'}>Type</th>
        <th className="bg-primary" key={'2h'}>Rating</th>
        <th className="bg-primary" key={'3h'}>Text</th>
        <th className="bg-primary" key={'4h'}>User</th>
        <th className="bg-primary" key={'5h'}>Entity</th>
      </tr>
    )
  }

  renderTable = () => {
    const rows = this.state.rows
    if (!rows) return null
    if (!rows.length) {
      return (
        <tr className="data">
          <td colSpan="6">
            <span className="text-muted">No feedback found</span>
          </td>
        </tr>
      )
    }
    return rows.map((row, index) => {
      return (
        <tr key={index} className="data">
          <td>{row.createdAt ? row.createdAt.toLocaleString() : ''}</td>
          <td><span className="badge text-bg-secondary">{row.get('type')}</span></td>
          <td>{renderRating(row)}</td>
          <td>{renderText(row)}</td>
          <td>{renderUserLink(row)}</td>
          <td>{renderEntityLink(row)}</td>
        </tr>
      )
    })
  }

  render = () => {
    return (
      <div>
        <Route path="/aiFeedback">
          <div className="container">
            <br/>
            <div>
              <h1 id="title">AI Feedback</h1>
            </div>
            <br/>
            {this.renderErrorBanner()}
            {this.renderFilterControls()}
            <table id="aifeedback" className="data">
              <tbody>
                {this.renderTableHeader()}
                {this.renderTable()}
              </tbody>
            </table>
            <br/>
            {this.numberOfResultsText()}
            <br/>
          </div>
        </Route>
      </div>
    )
  }
}
